/* eslint-disable no-case-declarations */
/* eslint-disable object-curly-newline */
/* eslint-disable no-param-reassign */
import { isEmpty, isNull } from 'lodash';
import { SecurityRead } from 'api';
import {
  CUMULATIVE_DIVIDENDS_ROW_NUMBER,
  INVESTMENT_DATE_ALIAS,
  SECURITY_NAME_ALIAS,
  SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO,
  SECURITY_TYPE_ALIAS,
  SECURITY_TYPE_ROW_NUMBER,
  SENIORITY_ALIAS,
  SHARES_OUTSTANDING_ALIAS,
  UNDERLYING_SECURITY_ALIAS,
} from 'common/constants/cap-table';
import * as validationMessages from 'common/constants/messages/validations';
import { OPTION, PREFERRED_STOCK as PREFERRED, WARRANT } from 'common/constants/securityTypes';
import { FundOwnershipDetail } from 'common/types/fundOwnership';
import { TableData } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { toString } from 'utilities';
import { getPreferredSecurities } from './getOptionsForSelect';
import { getOptions, isMissingUnderlyingSecurityValue, isNoneAsCommonValue } from './underlyingSecurities';
import { CapTableCell } from '../components/captable-table/CaptableTable.props';
import * as validationsConstants from '../data/validationsConstants';

const customValidations = ({
  cell,
  parsedColumns,
  tableData,
  addFeedback,
  addFeedbackFromCell,
  removeFeedback,
  removeFeedbackFromCell,
}: {
  cell: ICell,
  parsedColumns: Record<string, ICell>,
  tableData: TableData<CapTableCell> & { fund_ownership: { fund_ownership_detail: FundOwnershipDetail[] } },
  addFeedback: (message: string, validationType?: string) => void,
  addFeedbackFromCell: (targetCell: ICell, message: string) => void,
  removeFeedback: (message: string, validationType?: string) => void,
  removeFeedbackFromCell: (targetCell: ICell, message: string) => void,
}) => {
  const { alias, value, defaultValue, columnLegend, tooltipMessages } = cell;

  const { columns } = tableData;
  const preferredSecurities = getPreferredSecurities({ columns } as unknown as { columns: SecurityRead[] });
  const isSecurityType = (type: number) => {
    const securityType = parsedColumns[columnLegend! + SECURITY_TYPE_ROW_NUMBER];

    if (type && securityType?.value) {
      if (securityType.value.toString() === type.toString()) {
        return true;
      }
    }
    return false;
  };

  const isCumulativeDividendsYes = () => {
    const dividendsCell = parsedColumns[columnLegend! + CUMULATIVE_DIVIDENDS_ROW_NUMBER];
    return dividendsCell.value === '1';
  };

  const validateSecurityNameUniqueness = () => {
    // is the current cell a duplicate
    const currentNameValue = cell.value;

    // designed to stop iterating when the first duplicate is found
    const duplicateCell = Object.values(parsedColumns).find(tCell => {
      const isNameCell = tCell.alias === SECURITY_NAME_ALIAS;
      const isValidCellTypeForComparison = typeof tCell.value === 'string' && typeof currentNameValue === 'string';
      const isCurrentCell = tCell.key === cell.key;

      if (!isNameCell || !isValidCellTypeForComparison || isCurrentCell) {
        return false;
      }

      const securityNameMatches = tCell.value?.toString().toLowerCase() === currentNameValue.toLowerCase();

      return securityNameMatches;
    });

    const duplicateCellFound = !!duplicateCell;
    if (duplicateCellFound) {
      addFeedback(validationMessages.SECURITY_NAME_UNIQUE);
    } else {
      removeFeedbackFromCell(cell, validationMessages.SECURITY_NAME_UNIQUE);
    }
  };

  switch (alias) {
    case SECURITY_NAME_ALIAS: // 0
      if (isNull(value) || isEmpty(toString(value as string).trim())) {
        addFeedback('Create a name for this security.');
      } else if (value === defaultValue) {
        addFeedback(validationMessages.REPLACE_DEFAULT_VALUE);
      } else {
        removeFeedbackFromCell(cell, validationMessages.REPLACE_DEFAULT_VALUE);
        validateSecurityNameUniqueness();
      }

      break;
    case INVESTMENT_DATE_ALIAS: // 02
      if (!isCumulativeDividendsYes() && isSecurityType(PREFERRED)) {
        // remove required feedback from the investment date cell
        removeFeedbackFromCell(cell, validationMessages.IS_REQUIRED_ERROR);
      } else if (isCumulativeDividendsYes() && isSecurityType(PREFERRED) && !value) {
        addFeedbackFromCell(cell, validationMessages.IS_REQUIRED_ERROR);
      }
      break;
    case SECURITY_TYPE_ALIAS: // 03
      if (Number(value) <= 0) {
        addFeedback(validationMessages.SELECT_VALID_SECURITY);
      } else {
        removeFeedbackFromCell(cell, validationMessages.SELECT_VALID_SECURITY);
      }
      removeFeedbackFromCell(cell, SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO);
      const blockingTooltip = tooltipMessages?.find(
        message => message !== SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO
      );
      if (blockingTooltip) {
        addFeedback(blockingTooltip);
      }
      break;
    case SHARES_OUTSTANDING_ALIAS: // 05
      /*
        Warn (non-blocking) when a Preferred security's related fund ownership shares
        exceed its shares outstanding. The backend accepts this state (e.g. from ilevel
        sync), so it must not block re-saving server-accepted data; clear it whenever it
        no longer holds - including when the security stops being Preferred.
      */
      const relatedFundColumns = (tableData?.fund_ownership?.fund_ownership_detail ?? []).filter(
        column => column.security?.toString() === cell.columnId?.toString()
      );
      const totalFundOwnershipShares = relatedFundColumns.reduce((acc, curr) => acc + Number(curr.shares), 0);

      if (
        isSecurityType(PREFERRED)
        && relatedFundColumns.length
        && cell.value
        && Number(cell.value) < totalFundOwnershipShares
      ) {
        addFeedback(validationMessages.LESS_SHARES_IN_CAPTABLE_THAN_FUND_OWNERSHIP, validationMessages.WARNING_CLASSNAME);
      } else {
        removeFeedback(validationMessages.LESS_SHARES_IN_CAPTABLE_THAN_FUND_OWNERSHIP, validationMessages.WARNING_CLASSNAME);
      }
      break;

    case UNDERLYING_SECURITY_ALIAS: // 12
      const optionMessage = 'Please select the security that this option purchases when exercised.';
      const warrantMessage = 'Please select the security that this warrant purchases when exercised.';
      const isOption = isSecurityType(OPTION);
      const isWarrant = isSecurityType(WARRANT);
      const hasCommonSecurity = getOptions({
        securityType: OPTION,
        cells: parsedColumns as Record<string, CapTableCell>,
      }).length > 0;
      const isMissing = isMissingUnderlyingSecurityValue(value);
      const isInvalidNoneSelection = isNoneAsCommonValue(value) && (!isOption || hasCommonSecurity);

      if (isOption && (isMissing || isInvalidNoneSelection)) {
        addFeedback(optionMessage);
      } else {
        removeFeedbackFromCell(cell, optionMessage);
      }

      if (isWarrant && (isMissing || isInvalidNoneSelection)) {
        addFeedback(warrantMessage);
      } else {
        removeFeedbackFromCell(cell, warrantMessage);
      }
      break;
    case SENIORITY_ALIAS: // 15
      if (value === 'SELECT PRIORITY' || (value && Number(value) > preferredSecurities.length)) {
        addFeedback(validationsConstants.SELECT_VALID_LIQUIDATION_PRIORITY);
      } else {
        removeFeedbackFromCell(cell, validationsConstants.SELECT_VALID_LIQUIDATION_PRIORITY);
      }
      break;
    default:
      break;
  }
};

export default customValidations;
